// ── External Dependencies & Registrations
import { promises as fs } from 'node:fs';
import { promisify } from 'node:util';
import { brotliCompress, gzip } from 'node:zlib';

// ── Local (Development) Framework
import { logOperationHeader, logOperationSuccess, logStepHeader, readJSONFile, readTextFile, substituteText, writeTextFile } from '@/utilities';

// ── Initialisation ───────────────────────────────────────────────────────────────────────────────────────────────────

const gzipAsync = promisify(gzip);
const brotliAsync = promisify(brotliCompress);

// ── Types ────────────────────────────────────────────────────────────────────────────────────────────────────────────

interface Sizes {
    rendered: number;
    gzip: number;
    brotli: number;
}

interface NodePart {
    renderedLength: number;
    gzipLength: number;
    brotliLength: number;
}

interface NodeMeta {
    id: string;
    moduleParts: Record<string, string>;
}

interface VisualizerJson {
    nodeParts: Record<string, NodePart>;
    nodeMetas: Record<string, NodeMeta>;
}

// ── Constants ────────────────────────────────────────────────────────────────────────────────────────────────────────

const BUNDLE_START_MARKER = '<!-- BUNDLE_START -->';
const BUNDLE_END_MARKER = '<!-- BUNDLE_END -->';
const INDENT = '&nbsp;&nbsp;&nbsp;&nbsp;';
const BAR_WIDTH = 20;

// ── Actions ──────────────────────────────────────────────────────────────────────────────────────────────────────────

export async function documentBundleSizes(options?: { moduleLevel?: boolean }): Promise<void> {
    try {
        logOperationHeader('Document Bundle Sizes');

        logStepHeader('1️⃣  Read bundle analysis report');
        const json = await readJSONFile<VisualizerJson>('./bundle-analysis-reports/rollup-visualiser/index.json');

        logStepHeader(`2️⃣  Insert table into 'README.md'`);
        const distributionDirection = await detectDistributionDirection();
        const bundleTable = await buildBundleTable(json, distributionDirection, options?.moduleLevel ?? false);

        const readme = await readTextFile('./README.md');
        const updated = substituteText(readme, `\n${bundleTable}\n`, BUNDLE_START_MARKER, BUNDLE_END_MARKER);
        await writeTextFile('README.md', updated);

        logOperationSuccess('Bundle sizes documented');
    } catch (error) {
        console.error('❌  Error documenting bundle sizes', error);
        process.exit(1);
    }
}

// ── Helpers ──────────────────────────────────────────────────────────────────────────────────────────────────────────

async function detectDistributionDirection(): Promise<string> {
    try {
        await fs.access('./dist/client/assets');
        return './dist/client/assets';
    } catch {
        return './dist';
    }
}

function chunkSizes(sizes: Sizes): string {
    return `${formatBytes(sizes.rendered)} · gz ${formatBytes(sizes.gzip)} · br ${formatBytes(sizes.brotli)}`;
}

interface GroupData {
    sizes: Sizes;
    files: Map<string, Sizes>;
}

type GroupEntry = [string, GroupData];

async function buildBundleTable(json: VisualizerJson, distributionDirection: string, isModuleLevel: boolean): Promise<string> {
    const chunkGroups = buildChunkGroups(json);
    const bundlerTotal = chunkGroups
        .values()
        .flatMap((g) => g.values().toArray())
        .reduce((sum, g) => sum + g.sizes.rendered, 0);

    const distributionFiles = await readDistributionFileSizes(distributionDirection);
    distributionFiles.sort((a, b) => b[1].rendered - a[1].rendered);

    const lines = ['|Chunk/Module/File|Composition|', '|:------ |:-----------|'];

    for (const [file, sizes] of distributionFiles) {
        const groups = chunkGroups.get(file) ?? new Map<string, GroupData>();
        const sortedGroups = [...groups].toSorted((a, b) => b[1].sizes.rendered - a[1].sizes.rendered);

        const sectionLines =
            sortedGroups.length === 1
                ? renderSingleGroupSection(file, sizes, getSoleEntry(sortedGroups), bundlerTotal, isModuleLevel)
                : renderMultiGroupSection(file, sizes, sortedGroups, bundlerTotal, isModuleLevel);

        lines.push(...sectionLines);
    }

    return lines.join('\n');
}

function renderSingleGroupSection(file: string, sizes: Sizes, group: GroupEntry, bundlerTotal: number, isModuleLevel: boolean): string[] {
    const [groupName, { sizes: groupSizes, files }] = group;
    const groupPct = bundlerTotal > 0 ? (groupSizes.rendered / bundlerTotal) * 100 : 0;

    if (files.size === 1) {
        const fileName = getSoleFileName(files);
        return [`| ${file} → ${groupName} → ${fileName} | ${chunkSizes(sizes)} · ${bar(groupPct)} |`];
    }

    const lines = [`| ${file} → ${groupName} | ${chunkSizes(sizes)} · ${bar(groupPct)} |`];
    if (!isModuleLevel) lines.push(...renderFileRows(files, INDENT, bundlerTotal));
    return lines;
}

function renderMultiGroupSection(file: string, sizes: Sizes, sortedGroups: GroupEntry[], bundlerTotal: number, isModuleLevel: boolean): string[] {
    const lines = [`| ${file} | ${chunkSizes(sizes)} |`];

    for (const [groupName, { sizes: groupSizes, files }] of sortedGroups) {
        const groupPct = bundlerTotal > 0 ? (groupSizes.rendered / bundlerTotal) * 100 : 0;

        if (files.size === 1) {
            const fileName = getSoleFileName(files);
            lines.push(`| ${INDENT}${groupName} → ${fileName} | ${bar(groupPct)} |`);
            continue;
        }

        lines.push(`| ${INDENT}${groupName} | ${bar(groupPct)} |`);
        if (!isModuleLevel) lines.push(...renderFileRows(files, `${INDENT}${INDENT}`, bundlerTotal));
    }

    return lines;
}

function renderFileRows(files: Map<string, Sizes>, indent: string, bundlerTotal: number): string[] {
    const sortedFiles = [...files].toSorted((a, b) => b[1].rendered - a[1].rendered);
    return sortedFiles.map(([fileName, fileSizes]) => {
        const filePct = bundlerTotal > 0 ? (fileSizes.rendered / bundlerTotal) * 100 : 0;
        return `| ${indent}${fileName} | ${bar(filePct)} |`;
    });
}

function getSoleEntry<T>(entries: T[]): T {
    const [entry] = entries;
    if (entry === undefined) throw new Error('Expected exactly one entry');
    return entry;
}

function getSoleFileName(files: Map<string, Sizes>): string {
    const [fileName] = files.keys();
    if (fileName === undefined) throw new Error('Expected exactly one file');
    return fileName;
}

async function readDistributionFileSizes(distributionDirection: string): Promise<[string, Sizes][]> {
    // eslint-disable-next-line security/detect-non-literal-fs-filename -- distributionDirection is a build output path, not user input.
    const entries = await fs.readdir(distributionDirection);
    const jsFiles = entries.filter((f) => f.endsWith('.js') && !f.endsWith('.map'));

    return Promise.all(
        jsFiles.map(async (file) => {
            // eslint-disable-next-line security/detect-non-literal-fs-filename -- file comes from fs.readdir, not user input.
            const buffer = await fs.readFile(`${distributionDirection}/${file}`);
            const [gzipped, brotlied] = await Promise.all([gzipAsync(buffer), brotliAsync(buffer)]);
            return [file, { rendered: buffer.length, gzip: gzipped.length, brotli: brotlied.length }] as [string, Sizes];
        })
    );
}

function accumulateChunkPart(
    chunks: Map<string, Map<string, GroupData>>,
    json: VisualizerJson,
    groupName: string,
    fileName: string,
    chunkName: string,
    partUid: string
): void {
    const part = json.nodeParts[partUid];
    if (!part) return;
    const s = partSizes(part);

    let chunkGroups = chunks.get(chunkName);
    if (chunkGroups === undefined) {
        chunkGroups = new Map();
        chunks.set(chunkName, chunkGroups);
    }

    let group = chunkGroups.get(groupName);
    if (group === undefined) {
        group = { sizes: zero(), files: new Map<string, Sizes>() };
        chunkGroups.set(groupName, group);
    }

    addTo(group.sizes, s);

    let fileSizes = group.files.get(fileName);
    if (fileSizes === undefined) {
        fileSizes = zero();
        group.files.set(fileName, fileSizes);
    }
    addTo(fileSizes, s);
}

function buildChunkGroups(json: VisualizerJson): Map<string, Map<string, GroupData>> {
    const chunks = new Map<string, Map<string, GroupData>>();

    for (const meta of Object.values(json.nodeMetas)) {
        const groupName = sourceGroupName(meta.id);
        const fileName = shortModuleName(meta.id);

        for (const [chunkName, partUid] of Object.entries(meta.moduleParts)) {
            const chunkBaseName = chunkName.split('/').at(-1) ?? chunkName;
            accumulateChunkPart(chunks, json, groupName, fileName, chunkBaseName, partUid);
        }
    }

    return chunks;
}

function bar(pct: number): string {
    const count = Math.round((pct / 100) * BAR_WIDTH);
    return `\`${'█'.repeat(count)}${'░'.repeat(BAR_WIDTH - count)}\` ${pct.toFixed(1)}%`;
}

function sourceGroupName(id: string): string {
    const path = id.startsWith('/') ? id.slice(1) : id;
    if (path.startsWith('\u{0}')) return '(runtime)';
    if (path.startsWith('node_modules/')) {
        const rest = path.slice('node_modules/'.length);
        if (rest.startsWith('@')) {
            const parts = rest.split('/');
            return `${String(parts[0])}/${String(parts[1])}`;
        }
        return rest.split('/', 1)[0] ?? rest;
    }
    if (path.startsWith('rust/') || path.startsWith('__vite-plugin-wasm')) return 'wasm';
    return 'src';
}

function shortModuleName(id: string): string {
    const path = id.startsWith('/') ? id.slice(1) : id;
    if (path.startsWith('\u{0}')) return path.slice(1);
    if (path.startsWith('node_modules/')) {
        const rest = path.slice('node_modules/'.length);
        const parts = rest.startsWith('@') ? rest.split('/').slice(2) : rest.split('/').slice(1);
        return parts.join('/') || (rest.split('/').at(-1) ?? rest);
    }
    return path.split('/').at(-1) ?? path;
}

function partSizes(part: NodePart): Sizes {
    return { rendered: part.renderedLength, gzip: part.gzipLength, brotli: part.brotliLength };
}

function zero(): Sizes {
    return { rendered: 0, gzip: 0, brotli: 0 };
}

function addTo(target: Sizes, source: Sizes): void {
    target.rendered += source.rendered;
    target.gzip += source.gzip;
    target.brotli += source.brotli;
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${String(bytes)} B`;
    return `${(bytes / 1024).toFixed(1)} kB`;
}
